/* tslint:disable */
/* eslint-disable */
import { HttpClient, HttpContext, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';

import { AccountResponse } from '../models/account-response';
import { CreateAccountRequest } from '../models/create-account-request';
import { GenericResponse } from '../models/generic-response';
import { GetAccountResponse } from '../models/get-account-response';
import { UpdateAccountRequest } from '../models/update-account-request';

/**
 * Account APIs
 */
@Injectable({ providedIn: 'root' })
export class AccountService extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /** Path part for operation `getAllAccounts()` */
  static readonly GetAllAccountsPath = '/api/v1/accounts';

  /**
   * Get list of accounts.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getAllAccounts()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllAccounts$Response(
    params?: {
      /**
       * the page number.
       */
      page?: number;

      /**
       * the size of a page
       */
      size?: number;

      /**
       * sort
       */
      sort?: string;

      /**
       * This adds a string search to following fields:
       *   - account number
       *   - description
       */
      search?: string;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<GetAccountResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      AccountService.GetAllAccountsPath,
      'get'
    );
    if (params) {
      rb.query('page', params.page, {});
      rb.query('size', params.size, {});
      rb.query('sort', params.sort, {});
      rb.query('search', params.search, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<GetAccountResponse>;
        })
      );
  }

  /**
   * Get list of accounts.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getAllAccounts$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllAccounts(
    params?: {
      /**
       * the page number.
       */
      page?: number;

      /**
       * the size of a page
       */
      size?: number;

      /**
       * sort
       */
      sort?: string;

      /**
       * This adds a string search to following fields:
       *   - account number
       *   - description
       */
      search?: string;
    },
    context?: HttpContext
  ): Observable<GetAccountResponse> {
    return this.getAllAccounts$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<GetAccountResponse>): GetAccountResponse =>
          r.body
      )
    );
  }

  /** Path part for operation `createAccount()` */
  static readonly CreateAccountPath = '/api/v1/accounts';

  /**
   * create Account.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createAccount()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createAccount$Response(
    params: {
      body: CreateAccountRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<AccountResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      AccountService.CreateAccountPath,
      'post'
    );
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<AccountResponse>;
        })
      );
  }

  /**
   * create Account.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `createAccount$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createAccount(
    params: {
      body: CreateAccountRequest;
    },
    context?: HttpContext
  ): Observable<AccountResponse> {
    return this.createAccount$Response(params, context).pipe(
      map((r: StrictHttpResponse<AccountResponse>): AccountResponse => r.body)
    );
  }

  /** Path part for operation `getAccountById()` */
  static readonly GetAccountByIdPath = '/api/v1/accounts/{id}';

  /**
   * fetch Account details.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getAccountById()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAccountById$Response(
    params: {
      /**
       * The Account id that needs to be fetched
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<AccountResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      AccountService.GetAccountByIdPath,
      'get'
    );
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<AccountResponse>;
        })
      );
  }

  /**
   * fetch Account details.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getAccountById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAccountById(
    params: {
      /**
       * The Account id that needs to be fetched
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<AccountResponse> {
    return this.getAccountById$Response(params, context).pipe(
      map((r: StrictHttpResponse<AccountResponse>): AccountResponse => r.body)
    );
  }

  /** Path part for operation `deleteAccountById()` */
  static readonly DeleteAccountByIdPath = '/api/v1/accounts/{id}';

  /**
   * Delete Account  by id.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deleteAccountById()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteAccountById$Response(
    params: {
      /**
       * The Account id that needs to be delete
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<GenericResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      AccountService.DeleteAccountByIdPath,
      'delete'
    );
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<GenericResponse>;
        })
      );
  }

  /**
   * Delete Account  by id.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `deleteAccountById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteAccountById(
    params: {
      /**
       * The Account id that needs to be delete
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<GenericResponse> {
    return this.deleteAccountById$Response(params, context).pipe(
      map((r: StrictHttpResponse<GenericResponse>): GenericResponse => r.body)
    );
  }

  /** Path part for operation `updateAccount()` */
  static readonly UpdateAccountPath = '/api/v1/accounts/{id}';

  /**
   * update Account.
   *
   * update Account
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateAccount()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateAccount$Response(
    params: {
      /**
       * The Account id that needs to be updated
       */
      id: number;
      body: UpdateAccountRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<AccountResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      AccountService.UpdateAccountPath,
      'patch'
    );
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<AccountResponse>;
        })
      );
  }

  /**
   * update Account.
   *
   * update Account
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateAccount$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateAccount(
    params: {
      /**
       * The Account id that needs to be updated
       */
      id: number;
      body: UpdateAccountRequest;
    },
    context?: HttpContext
  ): Observable<AccountResponse> {
    return this.updateAccount$Response(params, context).pipe(
      map((r: StrictHttpResponse<AccountResponse>): AccountResponse => r.body)
    );
  }
}
